import { useCallback, useContext, useState } from 'react';
import api from 'api';
import { NDA_STATUSES } from 'models/Nda';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import useSocketEvent from 'services/socketService/hooks/useSocketEvent';
import { LISTEN_EVENTS } from 'services/socketService/socketEventNames';
import { NdaStatusUpdateEventData } from 'services/socketService/socketEventData';
import { sentryError } from 'utils';
import { useTranslation } from 'react-i18next';
import { LeadNdaProps } from 'models/Lead';
import {
  getIsNativeNda,
  getSigningPathWithReturnPath,
} from 'agreementUtils';
import useAgreementExecutionDownload from 'hooks/useAgreementExecutionDownload';
import { useLocation, useNavigate } from 'react-router-dom';

export type NativeNdaProps = Pick<
  LeadNdaProps,
  'ndaProvider' | 'agreementExecutionId' | 'agreementExecuted'
>;

export type UpdateLeadNdaProps = {
  manuallySetNDAStatus?: boolean;
} & Pick<LeadNdaProps, 'envelopeId' | 'signingStatus' | 'sentDate'> &
  NativeNdaProps;

export default function useNdaStatus(
  leadId: string,
  envelopeId: string,
  updateNdaProps?: (leadNdaProps: UpdateLeadNdaProps) => void,
  nativeNdaProps?: NativeNdaProps,
) {
  const [isLoading, setIsLoading] = useState(false);

  const { popSuccess, popServerError } = useContext(PopNotificationsContext);

  const { downloadExecution } = useAgreementExecutionDownload();

  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  const { t } = useTranslation();

  const { agreementExecutionId, agreementExecuted } = nativeNdaProps || {};

  const isNativeNda = getIsNativeNda(nativeNdaProps);

  const onSendNda = useCallback(
    async (config?: {
      message?: string;
      shouldChangeSentDate?: boolean;
      onSuccess?: () => void;
    }) => {
      const { message, shouldChangeSentDate = false, onSuccess } = config || {};

      if (!leadId) return;

      try {
        setIsLoading(true);

        const { data } = await api.portfolio.seller.sendEnvelope(
          leadId,
          message,
        );

        updateNdaProps?.({
          signingStatus: NDA_STATUSES.SENT,
          envelopeId: data.envelopeId,
          manuallySetNDAStatus: false,
          ndaProvider: data.ndaProvider,
          agreementExecutionId: data.agreementExecutionId,
          ...(shouldChangeSentDate && { sentDate: new Date() }),
        });

        popSuccess(t('successfullysentNDA'));
        onSuccess?.();
      } catch (e) {
        sentryError(e);
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    },
    [leadId, popServerError, popSuccess, t, updateNdaProps],
  );

  const sendNda = useCallback(
    (message?: string, onSuccess?: () => void) =>
      onSendNda({ message, shouldChangeSentDate: true, onSuccess }),
    [onSendNda],
  );

  const resendNda = useCallback(
    (onSuccess?: () => void) => onSendNda({ onSuccess }),
    [onSendNda],
  );

  const openNativeNda = useCallback(async () => {
    if (agreementExecuted) {
      await downloadExecution(agreementExecutionId);
      return;
    }

    const {
      data: { url },
    } = await api.agreements.getLeadSigningLink(leadId);

    navigate(getSigningPathWithReturnPath(url, `${pathname}${search}`));
  }, [
    agreementExecuted,
    agreementExecutionId,
    downloadExecution,
    leadId,
    navigate,
    pathname,
    search,
  ]);

  const getAndOpenNdaLink = useCallback(async () => {
    if (!leadId || (!isNativeNda && !envelopeId)) return;

    try {
      setIsLoading(true);

      if (isNativeNda) {
        await openNativeNda();
        return;
      }

      const {
        data: { url },
      } = await api.portfolio.seller.viewNda(leadId, envelopeId);

      window.location.href = url;
    } catch (e) {
      sentryError(e);
      popServerError(e);
    } finally {
      setIsLoading(false);
    }
  }, [envelopeId, isNativeNda, leadId, openNativeNda, popServerError]);

  const newStatus = useCallback(
    ({
      signingStatus,
      envelopeId,
      manuallySetNDAStatus,
      leadId: payloadLeadId,
      ndaProvider,
      agreementExecutionId,
      agreementExecuted,
    }: NdaStatusUpdateEventData) => {
      if (leadId !== payloadLeadId) return;

      updateNdaProps?.({
        signingStatus,
        envelopeId,
        manuallySetNDAStatus,
        ndaProvider,
        agreementExecutionId,
        agreementExecuted,
      });
    },
    [leadId, updateNdaProps],
  );

  useSocketEvent(LISTEN_EVENTS.NDA_STATUS_UPDATE, newStatus);

  return { isLoading, sendNda, resendNda, getAndOpenNdaLink };
}
